import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Typography, Space, Button, DatePicker, Tag, message } from 'antd';
import { SearchOutlined, TrophyOutlined, ExportOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { tenantStatsAPI } from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import { safeErrorMessage } from '../utils/errorHandler';
import { saveExcelResponse } from '../utils/downloadBlob';
import { DRUG_NAME_MAP, CALC_TYPE_LABELS } from '../constants/drugs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface UserStat {
  user_id: string;
  name: string;
  count: number;
}

interface DateStat {
  date: string;
  count: number;
}

interface TenantStatsData {
  total_calculations: number;
  by_type: Record<string, number>;
  by_drug: Record<string, number>;
  by_user: UserStat[];
  by_date: DateStat[];
  scope: 'personal' | 'tenant';
}

type DateRange = [Dayjs | null, Dayjs | null] | null;

const TenantStats: React.FC = () => {
  const { currentTenant, canManageCurrentTenant } = useTenant();
  const [data, setData] = useState<TenantStatsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(null);
  const [exporting, setExporting] = useState(false);

  const buildParams = () => {
    const params: Record<string, string> = {};
    if (dateRange?.[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1]) params.end_date = dateRange[1].format('YYYY-MM-DD');
    return params;
  };

  const fetchStats = async () => {
    if (!currentTenant) return;
    setData(null);
    setLoading(true);
    try {
      setData(await tenantStatsAPI.getStats(currentTenant.id, buildParams()) as TenantStatsData);
    } catch {
      message.error('加载统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchStats();
  }, [currentTenant]);

  const handleExport = async () => {
    if (!currentTenant || !canManageCurrentTenant) return;
    setExporting(true);
    try {
      const res = await tenantStatsAPI.exportExcel(currentTenant.id, buildParams());
      const startStr = dateRange?.[0]?.format('YYYY-MM-DD') || '';
      const endStr = dateRange?.[1]?.format('YYYY-MM-DD') || '';
      const datePart = startStr && endStr ? `${startStr}_${endStr}` : new Date().toISOString().slice(0, 7);
      await saveExcelResponse(res, `业务统计_${currentTenant.name || 'tenant'}_${datePart}.xlsx`);
      message.success('导出成功');
    } catch (err: unknown) {
      let detail: unknown = err || '导出失败';
      try {
        const responseData = (err as { response?: { data?: Blob | { detail?: unknown } } })?.response?.data;
        if (responseData instanceof Blob) {
          const text = await responseData.text();
          const json = JSON.parse(text) as { detail?: unknown };
          if (json.detail) detail = json.detail;
        } else if (responseData?.detail) {
          detail = responseData.detail;
        }
      } catch {
        // Keep the generic export error when the server response is not JSON.
      }
      message.error(safeErrorMessage(detail, '导出失败'));
    } finally {
      setExporting(false);
    }
  };

  if (!currentTenant) return <Text type="secondary">请先选择机构</Text>;

  return (
    <div>
      <Title level={4}>{canManageCurrentTenant ? '机构业务统计' : '我的业务统计'}</Title>
      <Space wrap style={{ marginBottom: 16 }}>
        <RangePicker onChange={(value) => setDateRange(value ? [value[0], value[1]] : null)} />
        <Button type="primary" onClick={() => void fetchStats()} loading={loading} icon={<SearchOutlined />}>
          查询
        </Button>
        {canManageCurrentTenant && (
          <Button icon={<ExportOutlined />} onClick={() => void handleExport()} loading={exporting}>
            导出Excel
          </Button>
        )}
      </Space>

      {data && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12} xl={6}>
              <Card style={{ borderTop: '3px solid #0B2545' }}>
                <Statistic title="总计算量" value={data.total_calculations} />
              </Card>
            </Col>
            {(['initial', 'optimize', 'pk_formula'] as const).map((key) => (
              <Col xs={24} md={12} xl={6} key={key}>
                <Card>
                  <Statistic title={CALC_TYPE_LABELS[key] || key} value={data.by_type?.[key] || 0} />
                </Card>
              </Col>
            ))}
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={canManageCurrentTenant ? 12 : 24}>
              <Card title="按药物" size="small">
                <Table
                  dataSource={Object.entries(data.by_drug || {}).map(([drug, count]) => ({ drug, count }))}
                  columns={[
                    {
                      title: '药物',
                      dataIndex: 'drug',
                      render: (value: string) => <Tag color="blue">{DRUG_NAME_MAP[value] || value}</Tag>,
                    },
                    { title: '次数', dataIndex: 'count' },
                  ]}
                  rowKey="drug"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
            {canManageCurrentTenant && (
              <Col xs={24} lg={12}>
                <Card
                  title={<><TrophyOutlined style={{ color: '#C5A55A', marginRight: 4 }} />成员排行榜</>}
                  size="small"
                >
                  <Table
                    dataSource={data.by_user || []}
                    columns={[
                      {
                        title: '排名',
                        key: 'rank',
                        width: 60,
                        render: (_value: unknown, _record: UserStat, index: number) => {
                          const colors = ['#C5A55A', '#bfbfbf', '#d48806'];
                          return index < 3
                            ? <Tag color={colors[index]}>{index + 1}</Tag>
                            : <Text type="secondary">{index + 1}</Text>;
                        },
                      },
                      { title: '成员', dataIndex: 'name' },
                      { title: '计算次数', dataIndex: 'count' },
                    ]}
                    rowKey="user_id"
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>
            )}
          </Row>

          {data.by_date?.length > 0 && (
            <Card title="日期趋势" size="small" style={{ marginTop: 16 }}>
              <Table
                dataSource={data.by_date}
                columns={[
                  { title: '日期', dataIndex: 'date' },
                  { title: '计算次数', dataIndex: 'count' },
                ]}
                rowKey="date"
                pagination={false}
                size="small"
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default TenantStats;
